import type { ReactElement } from "react"
import type { TypographyBreakpoint } from "@onestore/hel/dist/components/atoms/TextAtm/TextAtm.props"
import type Theme from "@onestore/hel/dist/typings/theme"
import getBaseMarkdownOverrides from "@gatsby-plugin-generic-page/helpers/getBaseMarkdownOverrides"
import type { UdsColor } from "@gatsby-plugin-generic-page/helpers/helTypographyToUds"
import { Link, Text as UdsText } from "@ionos-web-design-system/react"
import type { Components } from "react-markdown"
import type { SquidexMarkdownEmbeddedContent } from "~/types/squidex"
import BaseMarkdown from "../Base"
import { getRegExpBySchemaId } from "./contentType"
import EmbeddedContent from "./EmbeddedContent"

type Props = {
  children: string | SquidexMarkdownEmbeddedContent
  typography?: { bold: Theme.Typography; default: Theme.Typography }
  emphasis?: Theme.Emphasis
  typographyBreakpoint?: {
    default: TypographyBreakpoint
    bold: TypographyBreakpoint
  }
  components?: Components
  extraComponents?: Components
  pushSpace?: number
  hasOverridedSmallTypography?: boolean
  hasBoldedLinks?: boolean
  hasOpenedLinksInNewTab?: boolean
  alignment?: "left" | "center" | "right"
  color?: UdsColor
}

function Text({
  children: componentChildren,
  typography,
  emphasis,
  components,
  extraComponents,
  typographyBreakpoint,
  pushSpace,
  hasOverridedSmallTypography,
  hasBoldedLinks,
  hasOpenedLinksInNewTab,
  alignment,
  color,
}: Props): ReactElement<Props> {
  const baseMarkdownOverrides = getBaseMarkdownOverrides({
    typography: typographyBreakpoint?.default || {
      small: hasOverridedSmallTypography
        ? typography?.default || "small1"
        : "small1",
      medium: typography?.default || "medium1_h",
      large: typography?.default || "medium1_h",
    },
    boldTypography: typographyBreakpoint?.bold || {
      small: hasOverridedSmallTypography
        ? typography?.bold || "small4"
        : "small4",
      medium: typography?.bold || "medium3_h",
      large: typography?.bold || "medium3_h",
    },
    pushSpace: pushSpace ?? 1,
    emphasis,
    alignment,
    color,
  })

  const hasStringChildrenType = typeof componentChildren === "string"
  const text = hasStringChildrenType
    ? componentChildren
    : componentChildren.text

  return (
    <BaseMarkdown
      components={{
        ...(components || baseMarkdownOverrides),
        ...extraComponents,
        a: (({ href, children, title }) => {
          const regex = getRegExpBySchemaId(
            "video|cta-element|counter|marketplace-sale-configuration|static-price|cloudblue-domain|price-type-context-text|tooltip|modal-terms-link|term-link"
          )
          const match = href ? regex.exec(href) : null

          if (match && match.groups && !hasStringChildrenType) {
            const referenceId = match.groups.id
            const embeddedContent =
              componentChildren as SquidexMarkdownEmbeddedContent

            if (
              embeddedContent.contents?.some(
                (content) => content.id === referenceId
              )
            ) {
              return (
                <EmbeddedContent
                  referenceId={referenceId}
                  typography={typography?.default}
                  emphasis={emphasis}
                  {...componentChildren}
                />
              )
            }
          }

          if (
            !href ||
            href.startsWith("mailto:") ||
            /^[^\s/]+@[^\s/]+\.[^\s/]+$/.test(href)
          ) {
            return <>{children}</>
          }

          if (color) {
            return (
              <UdsText asChild color={color}>
                <a
                  className={`underline${hasBoldedLinks ? " font-bold" : ""}`}
                  href={href}
                  title={title || ""}
                  {...(hasOpenedLinksInNewTab && {
                    target: "_blank",
                    rel: "noopener noreferrer",
                  })}
                >
                  {children}
                </a>
              </UdsText>
            )
          }

          return (
            <Link
              concept="mono"
              className={hasBoldedLinks ? "font-bold" : ""}
              href={href}
              title={title || ""}
              {...(hasOpenedLinksInNewTab && {
                target: "_blank",
                rel: "noopener noreferrer",
              })}
            >
              {children}
            </Link>
          )
        }) as Components["a"],
      }}
    >
      {text}
    </BaseMarkdown>
  )
}

export default Text
